import axios from "axios";
import { safeLocalStorage as localStorage } from "@/lib/safeStorage";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let busyCount = 0;
let busyLabel = "";
const MUTATING = ["post", "put", "patch", "delete"];
const OPERATIONS = [[/pec/i, "Sto lavorando sulla PEC"], [/raccomandat|postal/i, "Sto inviando la raccomandata"], [/bollettin|pagopa/i, "Sto pagando il bollettino"], [/shipment|spedizion|pickup|ritiro/i, "Sto preparando la spedizione"], [/support|ticket/i, "Sto inviando il ticket"], [/gift/i, "Sto preparando la gift card"], [/airtime|ricaric|topup|reloadly/i, "Sto eseguendo la ricarica"], [/italiamobile/i, "Sto lavorando su Italia Mobile"], [/esim|airalo/i, "Sto attivando la eSIM"], [/greenpower|gp_|venergia|energ/i, "Sto lavorando sulla pratica luce e gas"], [/wallet|plafond/i, "Sto aggiornando il plafond"], [/payment|checkout|shop/i, "Sto preparando il pagamento"], [/valutazion|realestate/i, "Sto calcolando la valutazione"]];
const labelFor = (url) => (OPERATIONS.find(([re]) => re.test(url || "")) || [null, "Sto lavorando"])[1];
const tracked = (config) => !config?.silent && MUTATING.includes((config?.method || "").toLowerCase()) && !(config?.url || "").startsWith("/auth/");
const setBusy = (delta, url) => { busyCount = Math.max(0, busyCount + delta); if (delta > 0) busyLabel = labelFor(url); window.dispatchEvent(new CustomEvent("api-busy", { detail: { count: busyCount, label: busyLabel } })); };
api.interceptors.request.use((config) => { if (tracked(config)) { config.__busy = true; setBusy(1, config.url); } return config; });
api.interceptors.response.use((r) => { if (r.config?.__busy) { r.config.__busy = false; setBusy(-1); } return r; }, (e) => { if (e.config?.__busy) { e.config.__busy = false; setBusy(-1); } return Promise.reject(e); });

// Fallback cross-domain: se i cookie di terze parti sono bloccati (Safari/Firefox),
// inviamo il token salvato in localStorage come Bearer.
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("rv_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  if ((config.method || "").toLowerCase() === "post" && !config.headers["Idempotency-Key"] && window.crypto?.randomUUID) {
    config.headers["Idempotency-Key"] = window.crypto.randomUUID();
  }
  return config;
});

// Auto-refresh the short-lived access token on 401, then retry the request once.
let refreshPromise = null;
const AUTH_SKIP = ["/auth/refresh", "/auth/login", "/auth/register", "/auth/logout"];

api.interceptors.response.use(
  (response) => {
    if (!response.config?.url?.startsWith("/auth/") && ["post", "put", "patch", "delete"].includes(response.config?.method?.toLowerCase())) window.dispatchEvent(new Event("wallet-changed"));
    return response;
  },
  async (error) => {
    const { response, config } = error;
    if (response?.status === 403 && response?.data?.detail?.code === "compliance_required") window.dispatchEvent(new CustomEvent("compliance-required", { detail: response.data.detail }));
    const url = config?.url || "";
    if (
      response?.status === 401 &&
      config &&
      !config._retried &&
      !AUTH_SKIP.some((p) => url.includes(p))
    ) {
      config._retried = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        await refreshPromise;
        refreshPromise = null;
        return api(config);
      } catch (e) {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadPrivateDocument(doc) {
  const { data } = await api.get(`/files/${doc.storage_path.split("/").map(encodeURIComponent).join("/")}`, { responseType: "blob" });
  const { previewPdf } = await import("@/lib/pdfPreview");
  previewPdf(data, doc.original_filename || "documento");
}

export const euro = (n, options = {}) => {
  const max = options.maximumFractionDigits ?? 2;
  const min = Math.min(options.minimumFractionDigits ?? 2, max);
  const v = Math.round(Number(n || 0) * 10 ** max) / 10 ** max;
  const s = Math.abs(v).toLocaleString("it-IT", { useGrouping: true, ...options, style: "decimal", minimumFractionDigits: min, maximumFractionDigits: max });
  return `${v < 0 ? "-" : ""}€\u00a0${s}`;
};

export const euroText = (t) => String(t || "").replace(/(?<![\d.,])(\d+(?:\.\d{3})*(?:,\d+)?)\s?€/g, "€\u00a0$1");

export const eurIt = (v) => euro(v);
